import { create } from "zustand";
import { persist } from "zustand/middleware";

/**
 * Listings this person has already opened on this device.
 *
 * Purely a browsing aid: when the same object shows up again in search, the
 * feed or "similar", the card can say "Вы смотрели" so the user doesn't
 * re-open something they've already seen. Device-local and best-effort — it
 * doesn't need the backend and doesn't have to follow the account around.
 *
 * The list is capped at MAX_VIEWED (most-recent-first) so it can't grow without
 * bound in localStorage.
 */
const MAX_VIEWED = 300;

interface ViewedState {
  viewed: number[];
  markViewed: (id?: number | null) => void;
  clearViewed: () => void;
}

export const useViewedStore = create<ViewedState>()(
  persist(
    (set) => ({
      viewed: [],

      markViewed: (id) =>
        set((state) => {
          if (id == null || Number.isNaN(Number(id))) return state;
          const num = Number(id);
          const next = [num, ...state.viewed.filter((v) => v !== num)].slice(
            0,
            MAX_VIEWED,
          );
          return { viewed: next };
        }),

      clearViewed: () => set({ viewed: [] }),
    }),
    { name: "viewedListings" },
  ),
);

/**
 * Subscribes to the viewed list so a card re-renders the moment its listing is
 * opened elsewhere (e.g. in another tab, or after returning from the detail
 * page).
 */
export const useIsViewed = (id?: number | null): boolean =>
  useViewedStore((s) =>
    id == null ? false : s.viewed.includes(Number(id)),
  );
